/**
 * Collection Raf — filters, sort, mobile drawers, load more, sticky header offset
 */
(function () {
  'use strict';

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  /**
   * Keep sticky filter/sidebar flush under the Impulse sticky header.
   * Always use the header's live viewport bottom — never StickyHeaderWrap
   * (that spacer is taller and caused the mobile "hole").
   */
  function measureHeaderOffset(extra) {
    extra = parseInt(extra, 10) || 0;
    var header = document.querySelector('.site-header');
    if (!header || header.getAttribute('data-sticky') !== 'true') {
      return extra;
    }

    // Stuck but still translated off-screen (before --opening slides it in)
    if (
      header.classList.contains('site-header--stuck') &&
      !header.classList.contains('site-header--opening')
    ) {
      return extra;
    }

    var rect = header.getBoundingClientRect();
    // floor closes sub-pixel gaps; ignore when fully above the viewport
    var bottom = Math.floor(rect.bottom);
    if (bottom <= 0) return extra;
    return bottom + extra;
  }

  function syncStickyOffset(root) {
    if (!root) return;
    if (root.getAttribute('data-auto-header-offset') === 'false') {
      var manual = parseInt(root.getAttribute('data-sticky-extra') || '0', 10);
      if (isNaN(manual)) manual = 0;
      root.style.setProperty('--colraf-sticky-offset', manual + 'px');
      return;
    }

    var extra = parseInt(root.getAttribute('data-sticky-extra') || '0', 10);
    if (isNaN(extra)) extra = 0;

    var px = measureHeaderOffset(extra);
    root.style.setProperty('--colraf-sticky-offset', px + 'px');
  }

  function bindStickyOffset(root) {
    var ticking = false;
    function requestSync() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        syncStickyOffset(root);
      });
    }

    syncStickyOffset(root);
    window.addEventListener('scroll', requestSync, { passive: true });
    window.addEventListener('resize', requestSync);
    if (window.Shopify && Shopify.designMode) {
      setTimeout(requestSync, 300);
    }
  }

  function initGalleries(root) {
    root.querySelectorAll('[data-colraf-gallery]').forEach(function (gallery) {
      if (gallery.dataset.colrafGalleryReady === '1') return;
      gallery.dataset.colrafGalleryReady = '1';

      var track = gallery.querySelector('[data-colraf-gallery-track]');
      var dots = gallery.querySelectorAll('[data-colraf-dot]');
      if (!track || dots.length < 2) return;

      var slides = track.querySelectorAll('.colraf-card__slide');
      var suppressClick = false;

      function setActive(index) {
        if (index < 0) index = 0;
        if (index > dots.length - 1) index = dots.length - 1;
        for (var i = 0; i < dots.length; i++) {
          dots[i].classList.toggle('is-active', i === index);
        }
        for (var s = 0; s < slides.length; s++) {
          slides[s].tabIndex = s === index ? 0 : -1;
        }
      }

      function currentIndex() {
        var w = track.clientWidth || 1;
        return Math.round(track.scrollLeft / w);
      }

      var scrollTick = false;
      track.addEventListener(
        'scroll',
        function () {
          if (scrollTick) return;
          scrollTick = true;
          window.requestAnimationFrame(function () {
            scrollTick = false;
            setActive(currentIndex());
          });
        },
        { passive: true }
      );

      for (var d = 0; d < dots.length; d++) {
        (function (dot, index) {
          dot.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            var w = track.clientWidth || 1;
            track.scrollTo({ left: index * w, behavior: 'smooth' });
            setActive(index);
          });
        })(dots[d], d);
      }

      /* Avoid navigating to PDP when the gesture was a swipe */
      var startX = 0;
      var startY = 0;
      track.addEventListener(
        'touchstart',
        function (e) {
          if (!e.touches || !e.touches[0]) return;
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          suppressClick = false;
        },
        { passive: true }
      );
      track.addEventListener(
        'touchmove',
        function (e) {
          if (!e.touches || !e.touches[0]) return;
          var dx = Math.abs(e.touches[0].clientX - startX);
          var dy = Math.abs(e.touches[0].clientY - startY);
          if (dx > 8 && dx > dy) suppressClick = true;
        },
        { passive: true }
      );
      track.addEventListener(
        'click',
        function (e) {
          if (!suppressClick) return;
          e.preventDefault();
          e.stopPropagation();
          suppressClick = false;
        },
        true
      );
    });
  }

  function cartAddUrl() {
    if (window.theme && theme.routes && theme.routes.cartAdd) return theme.routes.cartAdd;
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root + (root.slice(-1) === '/' ? '' : '/') + 'cart/add.js';
  }

  function addVariantToCart(variantId) {
    return fetch(cartAddUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: Number(variantId),
        quantity: 1
      })
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) {
          var msg = (body && (body.description || body.message)) || 'Could not add to cart';
          throw new Error(msg);
        }
        return body;
      });
    });
  }

  function openCartDrawer() {
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));
  }

  function initQuickAdd(root) {
    if (!root || root.dataset.colrafQuickReady === '1') return;
    root.dataset.colrafQuickReady = '1';

    function getVariantData(card) {
      var el = card.querySelector('[data-colraf-variant-data]');
      if (!el) return [];
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return [];
      }
    }

    function optionKey(position) {
      if (String(position) === '1') return 'option1';
      if (String(position) === '2') return 'option2';
      if (String(position) === '3') return 'option3';
      return '';
    }

    function findVariant(variants, sizePos, sizeValue, colorPos, colorValue) {
      var sizeKey = optionKey(sizePos);
      var colorKey = optionKey(colorPos);
      var exactUnavailable = null;
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (sizeKey && v[sizeKey] !== sizeValue) continue;
        if (colorKey && colorValue && v[colorKey] !== colorValue) continue;
        if (v.available) return v;
        if (!exactUnavailable) exactUnavailable = v;
      }
      return exactUnavailable;
    }

    function syncSizesForColor(card, colorValue) {
      var variants = getVariantData(card);
      var sizePos = card.getAttribute('data-size-position');
      var colorPos = card.getAttribute('data-color-position');
      if (colorValue) card.setAttribute('data-selected-color', colorValue);

      card.querySelectorAll('[data-colraf-size]').forEach(function (btn) {
        var sizeValue = btn.getAttribute('data-size-value');
        var match = findVariant(variants, sizePos, sizeValue, colorPos, colorValue || card.getAttribute('data-selected-color'));
        btn.classList.remove('is-active');
        if (match && match.available) {
          btn.setAttribute('data-variant-id', String(match.id));
          btn.classList.remove('is-unavailable');
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
        } else {
          btn.removeAttribute('data-variant-id');
          btn.classList.add('is-unavailable');
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
        }
      });
    }

    function resetSizeButtons(card) {
      var color = card.getAttribute('data-selected-color') || '';
      var sizes = card.querySelector('[data-colraf-sizes]');
      if (sizes) sizes.classList.remove('is-loading');
      card.querySelectorAll('[data-colraf-size]').forEach(function (btn) {
        btn.classList.remove('is-loading', 'is-active');
      });
      if (card.querySelector('[data-colraf-size]')) {
        syncSizesForColor(card, color);
      }
    }

    function updateCardImage(card, imageUrl) {
      if (!imageUrl) return;
      var img = card.querySelector('.colraf-card__slide--single .colraf-card__img, .colraf-card__gallery .colraf-card__slide:first-child .colraf-card__img');
      if (!img) img = card.querySelector('.colraf-card__img:not(.colraf-card__img--secondary)');
      if (!img) return;
      if (img.tagName === 'IMG') {
        img.src = imageUrl;
        if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
      }
    }

    function closeAllQuick(exceptCard) {
      root.querySelectorAll('.colraf-card.is-quick-open').forEach(function (card) {
        if (exceptCard && card === exceptCard) return;
        card.classList.remove('is-quick-open');
        var sizes = card.querySelector('[data-colraf-sizes]');
        var trigger = card.querySelector('[data-colraf-quick-trigger]');
        if (sizes) sizes.hidden = true;
        if (trigger) trigger.hidden = false;
        resetSizeButtons(card);
      });
    }

    function openSizes(card) {
      closeAllQuick(card);
      var sizes = card.querySelector('[data-colraf-sizes]');
      var trigger = card.querySelector('[data-colraf-quick-trigger]');
      if (!sizes) return;
      resetSizeButtons(card);
      card.classList.add('is-quick-open');
      if (trigger) trigger.hidden = true;
      sizes.hidden = false;
    }

    function setLoading(card, on, activeBtn) {
      var sizes = card.querySelector('[data-colraf-sizes]');
      if (sizes) sizes.classList.toggle('is-loading', !!on);
      card.querySelectorAll('[data-colraf-size]').forEach(function (btn) {
        var isChosen = !!(on && activeBtn && btn === activeBtn);
        btn.classList.toggle('is-loading', isChosen);
        btn.classList.toggle('is-active', isChosen);
        if (on) {
          btn.disabled = true;
        } else if (btn.classList.contains('is-unavailable') || !btn.getAttribute('data-variant-id')) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
        } else {
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
        }
      });
    }

    function selectSwatch(swatch) {
      if (!swatch || swatch.disabled || swatch.classList.contains('is-unavailable')) return;
      var swatchCard = swatch.closest('.colraf-card');
      if (!swatchCard) return;

      try {
        var colorValue = swatch.getAttribute('data-color-value') || '';
        swatchCard.querySelectorAll('[data-colraf-swatch]').forEach(function (s) {
          var on = s === swatch;
          s.classList.toggle('is-active', on);
          s.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        syncSizesForColor(swatchCard, colorValue);

        var imageUrl = swatch.getAttribute('data-image');
        if (imageUrl && swatchCard.getAttribute('data-colraf-lock-image') !== 'true') {
          window.requestAnimationFrame(function () {
            updateCardImage(swatchCard, imageUrl);
          });
        }

        var direct = swatchCard.querySelector('[data-colraf-quick-direct]');
        if (direct) {
          var variants = getVariantData(swatchCard);
          var colorPos = swatchCard.getAttribute('data-color-position');
          var colorKey = optionKey(colorPos);
          var found = null;
          for (var i = 0; i < variants.length; i++) {
            if (colorKey && variants[i][colorKey] === colorValue && variants[i].available) {
              found = variants[i];
              break;
            }
          }
          if (found) direct.setAttribute('data-variant-id', String(found.id));
        }
      } catch (err) {
        if (typeof console !== 'undefined' && console.warn) {
          console.warn('[Collection Raf] Swatch select failed', err);
        }
      }

      if (typeof swatch.blur === 'function') swatch.blur();
    }

    root.addEventListener(
      'click',
      function (e) {
        var swatch = e.target.closest('[data-colraf-swatch]');
        if (swatch && root.contains(swatch)) {
          e.preventDefault();
          e.stopPropagation();
          selectSwatch(swatch);
          return;
        }

        var trigger = e.target.closest('[data-colraf-quick-trigger]');
        if (trigger && root.contains(trigger)) {
          e.preventDefault();
          e.stopPropagation();
          var card = trigger.closest('.colraf-card');
          if (!card) return;

          if (trigger.hasAttribute('data-colraf-quick-direct')) {
            var directId = trigger.getAttribute('data-variant-id');
            if (!directId || trigger.disabled) return;
            trigger.disabled = true;
            addVariantToCart(directId)
              .then(function () {
                openCartDrawer();
              })
              .catch(function (err) {
                if (typeof console !== 'undefined' && console.warn) {
                  console.warn('[Collection Raf] Quick Add failed', err);
                }
              })
              .then(function () {
                trigger.disabled = false;
              });
            return;
          }

          openSizes(card);
          return;
        }

        var sizeBtn = e.target.closest('[data-colraf-size]');
        if (sizeBtn && root.contains(sizeBtn)) {
          e.preventDefault();
          e.stopPropagation();
          if (sizeBtn.disabled || sizeBtn.classList.contains('is-unavailable') || !sizeBtn.getAttribute('data-variant-id')) {
            return;
          }
          var sizeCard = sizeBtn.closest('.colraf-card');
          if (!sizeCard) return;
          var variantId = sizeBtn.getAttribute('data-variant-id');
          setLoading(sizeCard, true, sizeBtn);
          addVariantToCart(variantId)
            .then(function () {
              openCartDrawer();
              closeAllQuick();
            })
            .catch(function (err) {
              if (typeof console !== 'undefined' && console.warn) {
                console.warn('[Collection Raf] Quick Add failed', err);
              }
              setLoading(sizeCard, false);
            });
          return;
        }

        if (!e.target.closest('.colraf-card.is-quick-open')) {
          closeAllQuick();
        }
      },
      true
    );

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAllQuick();
    });
  }

  function initRoot(root) {
    if (!root || root.dataset.colrafReady === '1') return;
    root.dataset.colrafReady = '1';

    var debounceMs = 350;
    var filterTimer = null;

    bindStickyOffset(root);
    initGalleries(root);
    initQuickAdd(root);

    /* —— Accordion —— */
    root.querySelectorAll('[data-colraf-filter-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var group = btn.closest('[data-colraf-filter-group]');
        if (!group) return;
        var panel = group.querySelector('.colraf-filters__panel');
        var open = group.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (panel) {
          if (open) panel.removeAttribute('hidden');
          else panel.setAttribute('hidden', '');
        }
      });
    });

    /* —— Filter submit —— */
    function submitFilters(form) {
      if (!form) return;
      var sortSelect = root.querySelector('[data-colraf-sort-select]');
      var hiddenSort = form.querySelector('[data-colraf-sort-hidden]');
      if (sortSelect && hiddenSort) {
        hiddenSort.value = sortSelect.value;
      } else if (sortSelect && !hiddenSort && sortSelect.value) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'sort_by';
        input.value = sortSelect.value;
        input.setAttribute('data-colraf-sort-hidden', '');
        form.appendChild(input);
      }
      form.submit();
    }

    root.querySelectorAll('[data-colraf-filters]').forEach(function (form) {
      form.addEventListener('change', function (e) {
        if (!e.target.matches('[data-colraf-filter-input]')) return;
        clearTimeout(filterTimer);
        filterTimer = setTimeout(function () {
          submitFilters(form);
        }, debounceMs);
      });
    });

    /* —— Sort (desktop select) —— */
    var sortSelect = root.querySelector('[data-colraf-sort-select]');
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        var params = new URLSearchParams(window.location.search);
        params.set('sort_by', sortSelect.value);
        params.delete('page');
        var url = window.location.pathname + '?' + params.toString();
        window.location.href = url;
      });
    }

    /* —— Mobile drawers —— */
    var filtersDrawer = root.querySelector('[data-colraf-filters-drawer]');
    var sortDrawer = root.querySelector('[data-colraf-sort-drawer]');

    function openDrawer(drawer) {
      if (!drawer) return;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('colraf-drawer-open');
    }

    function closeDrawer(drawer) {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (
        !root.querySelector('.colraf-drawer.is-open')
      ) {
        document.body.classList.remove('colraf-drawer-open');
      }
    }

    function closeAllDrawers() {
      closeDrawer(filtersDrawer);
      closeDrawer(sortDrawer);
    }

    var openFiltersBtn = root.querySelector('[data-colraf-open-filters]');
    var openSortBtn = root.querySelector('[data-colraf-open-sort]');
    if (openFiltersBtn) {
      openFiltersBtn.addEventListener('click', function () {
        openDrawer(filtersDrawer);
      });
    }
    if (openSortBtn) {
      openSortBtn.addEventListener('click', function () {
        openDrawer(sortDrawer);
      });
    }

    root.querySelectorAll('[data-colraf-close-drawer]').forEach(function (el) {
      el.addEventListener('click', function () {
        closeDrawer(el.closest('.colraf-drawer'));
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAllDrawers();
    });

    /* Mobile sort options */
    root.querySelectorAll('[data-colraf-sort-option]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var value = btn.getAttribute('data-colraf-sort-option');
        var params = new URLSearchParams(window.location.search);
        params.set('sort_by', value);
        params.delete('page');
        window.location.href = window.location.pathname + '?' + params.toString();
      });
    });

    /* —— Load more (client-side reveal; all products already in DOM) —— */
    var loadBtn = root.querySelector('[data-colraf-load-more]');
    var grid = root.querySelector('[data-colraf-grid]');
    var showingEl = root.querySelector('[data-colraf-showing]');
    var moreWrap = root.querySelector('.colraf__more');

    function getPageSize() {
      return parseInt(root.getAttribute('data-page-size') || '24', 10) || 24;
    }

    function getTotalCount() {
      return parseInt(root.getAttribute('data-total') || '0', 10) || 0;
    }

    function getDeferredItems() {
      return grid ? grid.querySelectorAll('[data-colraf-deferred]') : [];
    }

    function getShownCount() {
      if (!grid) return 0;
      var items = grid.querySelectorAll('.colraf__grid-item');
      var shown = 0;
      for (var i = 0; i < items.length; i++) {
        if (!items[i].hasAttribute('data-colraf-deferred')) shown += 1;
      }
      return shown;
    }

    function updateShowing() {
      if (!showingEl) return;
      var total = getTotalCount();
      var shown = getShownCount();
      var template = root.getAttribute('data-showing-template') || 'Showing [shown] of [total] products';
      showingEl.textContent = template
        .replace(/\{\{\s*shown\s*\}\}/g, String(shown))
        .replace(/\{\{\s*total\s*\}\}/g, String(total))
        .replace('[shown]', String(shown))
        .replace('[total]', String(total));
    }

    function showLoadMoreEnabled() {
      return root.getAttribute('data-load-more') === 'true';
    }

    function ensureLoadButton() {
      var btn = root.querySelector('[data-colraf-load-more]');
      if (btn) {
        btn.hidden = false;
        btn.disabled = false;
        return btn;
      }
      if (!moreWrap || !showLoadMoreEnabled()) return null;
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'colraf__load-more';
      btn.setAttribute('data-colraf-load-more', '');
      btn.textContent = root.getAttribute('data-load-more-label') || 'LOAD MORE';
      if (showingEl && showingEl.parentNode === moreWrap) {
        moreWrap.insertBefore(btn, showingEl);
      } else {
        moreWrap.insertBefore(btn, moreWrap.firstChild);
      }
      bindLoadMore(btn);
      return btn;
    }

    function syncLoadMoreVisibility() {
      if (!showLoadMoreEnabled()) {
        var existing = root.querySelector('[data-colraf-load-more]');
        if (existing) existing.remove();
        return;
      }
      var deferred = getDeferredItems();
      var btn = root.querySelector('[data-colraf-load-more]');
      if (!deferred.length || getShownCount() >= getTotalCount()) {
        if (btn) btn.remove();
        return;
      }
      ensureLoadButton();
    }

    function bindLoadMore(btn) {
      if (!btn || btn.dataset.colrafBound === '1') return;
      btn.dataset.colrafBound = '1';
      btn.addEventListener('click', onLoadMoreClick);
    }

    function onLoadMoreClick() {
      if (!grid) return;
      var deferred = getDeferredItems();
      if (!deferred.length) {
        syncLoadMoreVisibility();
        return;
      }

      var batch = getPageSize();
      var revealed = 0;
      for (var i = 0; i < deferred.length && revealed < batch; i++) {
        deferred[i].removeAttribute('hidden');
        deferred[i].removeAttribute('data-colraf-deferred');
        revealed += 1;
      }

      initGalleries(root);
      updateShowing();
      syncLoadMoreVisibility();
    }

    if (grid) {
      if (loadBtn) bindLoadMore(loadBtn);
      updateShowing();
      syncLoadMoreVisibility();
    }
  }

  function boot() {
    document.querySelectorAll('[data-colraf]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-colraf]');
    if (root) {
      delete root.dataset.colrafReady;
      initRoot(root);
    }
  });
})();
